// built-from: /specialist-activity-handoff v1 impl:specialist-activity-handoff role:implementation
export type EngagementSpecialistId =
  | 'value-stream-mapping'
  | 'client-vision'
  | 'objectives'
  | 'domains-stakeholders'
  | 'estate-discovery'
  | 'integration-mapping'
  | 'event-storming'
  | 'cloud-architecture-design'
  | 'cloud-cost-estimation'
  | 'rom-estimation'
  | 'migration-roadmap';

export interface EngagementSpecialistDefinition {
  id: EngagementSpecialistId;
  label: string;
  routePart: string;
}

export const ENGAGEMENT_SPECIALISTS: readonly EngagementSpecialistDefinition[] = Object.freeze([
  {
    id: 'value-stream-mapping',
    label: 'Value-stream mapping',
    routePart: 'value-stream-mapping',
  },
  {
    id: 'client-vision',
    label: 'Client vision capture',
    routePart: 'client-vision',
  },
  {
    id: 'objectives',
    label: 'Objectives',
    routePart: 'objectives',
  },
  {
    id: 'domains-stakeholders',
    // 095: renamed to Business map; the key stays domains-stakeholders in
    // the pool and on routes.
    label: 'Business map',
    routePart: 'domains-stakeholders',
  },
  // 082: the notes-shaped catalogue activities share ONE generic workspace
  // (routePart 'notes'); each evolves onto its own route when it grows a
  // richer specialist. Value-stream-mapping keeps its voice-led workspace.
  { id: 'estate-discovery', label: 'Estate Discovery', routePart: 'notes' },
  { id: 'integration-mapping', label: 'Integration Landscape Mapping', routePart: 'notes' },
  // 083: Event Storming has its own step-flow capture workspace.
  { id: 'event-storming', label: 'Event Storming', routePart: 'event-storming' },
  { id: 'cloud-architecture-design', label: 'Cloud Architecture Design', routePart: 'platform-architecture' },
  { id: 'cloud-cost-estimation', label: 'Cloud Cost Estimation', routePart: 'notes' },
  { id: 'rom-estimation', label: 'ROM Estimation', routePart: 'notes' },
  { id: 'migration-roadmap', label: 'Prioritised Migration Roadmap', routePart: 'notes' },
]);

export const CURRENT_UNSUPPORTED_SPECIALIST_OPTION = '__current-unsupported__';

const LEGACY_SPECIALIST_IDS: Readonly<Record<string, EngagementSpecialistId>> = Object.freeze({
  'Voice-led value-stream mapping': 'value-stream-mapping',
});

export function normaliseEngagementSpecialistId(value?: string | null): string | null {
  const candidate = typeof value === 'string' ? value.trim() : '';
  if (!candidate) return null;
  return LEGACY_SPECIALIST_IDS[candidate] || candidate;
}

export function engagementSpecialistFor(
  value?: string | null,
): EngagementSpecialistDefinition | undefined {
  const id = normaliseEngagementSpecialistId(value);
  return ENGAGEMENT_SPECIALISTS.find((specialist) => specialist.id === id);
}

export function engagementSpecialistLabel(value?: string | null): string {
  const id = normaliseEngagementSpecialistId(value);
  if (!id) return 'No specialist workspace';
  return engagementSpecialistFor(id)?.label || 'Unavailable workspace';
}

export function engagementActivityDestination(activity: {
  id: string;
  specialistMethod?: string | null;
}): string {
  const base = `/engagement/activities/${encodeURIComponent(activity.id)}`;
  const specialist = engagementSpecialistFor(activity.specialistMethod);
  return specialist ? `${base}/specialist/${specialist.routePart}` : base;
}
